import { ChangeEvent, FC, useRef, useState } from 'react'
import { ButtonIcon } from '../Button/ButtonIcon'
import { IconPaths, IconSize } from '../../../_metronic/helpers'
import styles from './AudioPlayer.module.css'

type IAudioPlayer = {
  src: string
  fileName?: string
}

const formatTime = (seconds: number) => {
  if (!Number.isFinite(seconds)) return '0:00'
  const minutes = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

export const AudioPlayer: FC<IAudioPlayer> = ({ src, fileName = 'audio.mp4' }) => {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  const togglePlay = () => {
    const audio = audioRef.current
    if (!audio) return
    if (audio.paused) audio.play()
    else audio.pause()
  }

  const seek = (event: ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current
    if (!audio) return
    audio.currentTime = Number(event.target.value)
    setCurrentTime(audio.currentTime)
  }

  const handleDownload = () => {
    const link = document.createElement('a')
    link.href = src
    link.download = fileName
    link.click()
  }

  return (
    <div className='d-flex align-items-center gap-2'>
      <ButtonIcon
        icon={isPlaying ? IconPaths.Pause : IconPaths.Play}
        onClick={togglePlay}
        ariaLabel={isPlaying ? 'Pausar' : 'Reproduzir'}
        className={`btn-icon rounded-circle flex-shrink-0 ${styles.iconButton}`}
        style={{ width: 32, height: 32 }}
        iconSize={IconSize.Small}
      />

      <input
        type='range'
        min={0}
        max={duration || 0}
        step={0.1}
        value={currentTime}
        onChange={seek}
        className='form-range'
        style={{ width: 130 }}
      />

      <span className='fs-7 text-muted text-nowrap' style={{ minWidth: 30 }}>
        {formatTime(currentTime > 0 ? currentTime : duration)}
      </span>

      <ButtonIcon
        icon={IconPaths.Download}
        onClick={handleDownload}
        ariaLabel='Baixar áudio'
        className={`btn-icon btn-sm rounded-circle flex-shrink-0 ${styles.iconButton}`}
        style={{ width: 32, height: 32 }}
      />

      <audio
        ref={audioRef}
        src={src}
        style={{ display: 'none' }}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
      />
    </div>
  )
}
